import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

// Share platforms configuration
const SHARE_PLATFORMS = [
  {
    name: 'WhatsApp',
    icon: WhatsAppIcon,
    color: '#25D366',
    getUrl: (text, url) => `https://wa.me/?text=${encodeURIComponent(`${text}\n\n${url}`)}`,
  },
  {
    name: 'Facebook',
    icon: FacebookIcon,
    color: '#1877F2',
    getUrl: (text, url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  {
    name: 'Twitter',
    icon: TwitterIcon,
    color: '#1DA1F2',
    getUrl: (text, url) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
];

function ShareDialog({
  open,
  onClose,
  prayer,
  appUrl = 'https://pray4all.app',
}) {
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  if (!prayer) return null;

  // Generate shareable content
  const prayerText = prayer.content?.substring(0, 200) + (prayer.content?.length > 200 ? '...' : '');
  const shareText = `"${prayerText}"\n\nShared from Pray4All - A community united in prayer`;
  const shareUrl = `${appUrl}/prayers/${prayer.id}`;

  // Handle native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Prayer from Pray4All',
          text: shareText,
          url: shareUrl,
        });
        onClose();
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  // Handle platform share
  const handlePlatformShare = (platform) => {
    const url = platform.getUrl(shareText, shareUrl);
    window.open(url, '_blank', 'width=600,height=400');
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setSnackbar({ open: true, message: 'Link copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setSnackbar({ open: true, message: 'Link copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Check if native share is supported
  const hasNativeShare = typeof navigator !== 'undefined' && navigator.share;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
            }}
          >
            Share Prayer
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Prayer preview */}
          <Box
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              p: 2,
              mb: 3,
              borderLeft: 4,
              borderColor: 'primary.main',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontStyle: 'italic',
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              "{prayerText}"
            </Typography>
          </Box>

          {/* Native share button (mobile) */}
          {hasNativeShare && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleNativeShare}
              sx={{
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Share...
            </Button>
          )}

          {hasNativeShare && <Divider sx={{ my: 2 }}>Or share via</Divider>}

          {/* Social share buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            {SHARE_PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              return (
                <IconButton
                  key={platform.name}
                  onClick={() => handlePlatformShare(platform)}
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: `${platform.color}15`,
                    color: platform.color,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: platform.color,
                      color: 'white',
                      transform: 'scale(1.1)',
                    },
                  }}
                  aria-label={`Share on ${platform.name}`}
                >
                  <Icon sx={{ fontSize: 28 }} />
                </IconButton>
              );
            })}
          </Box>

          {/* Copy link section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              bgcolor: 'grey.100',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.secondary',
              }}
            >
              {shareUrl}
            </Typography>
            <Button
              variant={copied ? 'contained' : 'outlined'}
              size="small"
              onClick={handleCopyLink}
              startIcon={copied ? <CheckIcon /> : <CopyIcon />}
              color={copied ? 'success' : 'primary'}
              sx={{
                minWidth: 100,
                textTransform: 'none',
                borderRadius: 1.5,
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ShareDialog;
